import * as React from "react";
import { Box, Button, InputLabel, TextField, InputAdornment, Typography, Skeleton, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventIcon from '@mui/icons-material/Event';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CommentIcon from '@mui/icons-material/Comment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  ButtonsContainer,
  ErrorText,
  FieldContainer,
  FormContainer,
  FixedHeightBody,
  ScrollRegion,
} from "./AddPaymentPopup.styled";
import InputField from "../../../../components/formFields/InputField";
import { ModalWindow } from "../../../../shared/components/modalWindow/ModalWindow";
import useFetchWithMsal from "../../../../utils/useFetchWithMsal";
import { LoadingPanel } from "../../../../shared/components/LoadingPanel";
import StyledGrid, { FilterChip, StyledTextSearch } from "../../../../shared/components/StyledGrid";
import { useAddPaymentMutation, useGetInvociesMutation, useGetInvoicesStatusesQuery, useGetPaymentsMutation } from "../../../../services/InsuranceService";
import moment from "moment";
import { TableFilter, assessmentClasses, emailStatuses } from "../../invoicesTab/tableFilter/TableFilter";
import { ApplyPaymentModal } from "./ApplyPaymentModal";
import PaymentHistoryModal from "../PaymentHistoryModal";
import { findIdByName } from "../../invoicesTab/InvoicesTab";
import { buildInvoicesBaseColumns } from "../../invoicesTab/invoicesColumns";
import { useSelector } from "react-redux";

// Destructure frequently used React hooks for cleaner usage below
const { useState, useEffect } = React;

export const AddPaymentPopup = ({ open, onClose, data: initialData }) => {
  // Ref to trigger Formik submit programmatically on unlink/apply actions
  const formikRef = React.useRef(null);
  // Ref to anchor Snackbar to the modal content
  const modalContentRef = React.useRef(null);
  // Track a mutable payment id (supports newly created payments that return an id from API)
  const [currentPaymentId, setCurrentPaymentId] = useState(initialData?.id || null);
  const isNew = !initialData;
  // Add appliedAmount baseline to prevent initial programmatic sync from marking form dirty.
  const initialAppliedFromData = Array.isArray(initialData?.invoices)
    ? initialData.invoices.reduce(
      (sum, inv) =>
        sum + Number(inv?.appliedAmount ?? inv?.AppliedAmount ?? 0),
      0
    )
    : Number(initialData?.appliedAmount ?? 0);

  const initialValues = React.useMemo(
    () =>
      initialData
        ? {
          ...initialData,
          appliedAmount: Number.isFinite(initialAppliedFromData)
            ? initialAppliedFromData
            : 0,
        }
        : {
          paymentAmount: "",
          comments: "",
          paymentType: "",
          dateReceived: moment().format("YYYY-MM-DD"),
          appliedAmount: 0,
        },
    [initialData, initialAppliedFromData]
  );

  React.useEffect(() => {
    /* eslint-disable no-console */
    console.debug('[AddPaymentPopup] open test:', open, 'initialData:', initialData);
    // userId and roleId are logged separately after selectors are initialized
    /* eslint-enable no-console */
  }, [initialData, open]);

  const { data: invoicesStatuses, isLoading: isLoadingStatuses } = useGetInvoicesStatusesQuery("");
  const filteredStatuses = invoicesStatuses?.filter((s) => s.id !== -1) || [];

  const { naicCode, memberName, status, statusIds, assessmentDate, assessmentId, invoiceNumber, assessmentClass, emailStatus, groupName, paymentOpenStatus } = useSelector(
    (state) => state.filter
  );

  const baseColumns = React.useMemo(
    () => buildInvoicesBaseColumns({ invoicesStatuses, filteredStatuses, readOnlyStatus: true, hideStatusCaret: true }),
    [invoicesStatuses, filteredStatuses]
  );
  const appliedAmountCol = {
    field: "appliedAmount",
    headerName: "Applied Amount",
    editable: false,
    width: 100,
    renderCell: (params) => {
      const sel = selectedInvoices?.find((item) => item.id === params.row.id);
      if (sel) {
        const amt = Number(sel.appliedAmount || 0);
        return (
          <Typography sx={{ width: '100%', textAlign: 'center' }}>
            {amt ? amt.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : ''}
          </Typography>
        );
      }
      return '';
    },
  };
  const byField = (f) => baseColumns.find((c) => c.field === f);
  // Combined NAIC + Member column
  const naicMemberCol = {
    field: 'naicMember',
    headerName: 'NAIC - Member',
    flex: 1,
    minWidth: 220,
    // Use renderCell to derive value directly so we are not dependent on valueGetter when rows are hydrated dynamically
    renderCell: (params) => {
      const naic = params?.row?.naicCode || '';
      const member = params?.row?.memberName || '';
      const combined = naic && member ? `${naic} - ${member}` : (naic || member || '');
      return (
        <Box sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          lineHeight: 1.2,
          py: 0.25,
        }}>
          {combined}
        </Box>
      );
    }
  };
  const invoicesColumns = [
    appliedAmountCol,
    naicMemberCol,
    byField('assessmentTitle'),
    byField('invoiceDate'),
    byField('noticeNumber'),
    byField('status'),
  ].filter(Boolean);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [totalCount, setTotalCount] = useState(0);

  const [sortingModel, setSortingModel] = useState();
  // Normalize selected invoices so each item contains both a UI-facing
  // `appliedAmount` (camelCase) and the backend-facing `AppliedAmount` and
  // `Note` fields. This avoids breaking existing UI while adding the new
  // properties required by the API.
  const normalizeSelected = (arr) => {
    if (!arr) return [];
    return arr.map((it) => {
      const id = it.invoiceId ?? it.id ?? it.invoiceId;
      const appliedAmountValue = (it.appliedAmount !== undefined) ? Number(it.appliedAmount) : (it.AppliedAmount !== undefined ? Number(it.AppliedAmount) : (it.amount || 0));
      return {
        id,
        appliedAmount: appliedAmountValue || "",
        AppliedAmount: appliedAmountValue || 0,
        Note: it.Note || it.note || "",
      };
    });
  };

  // Local selection state: invoices the user is applying amounts to (editable working set)
  const [selectedInvoices, setSelectedInvoices] = useState(() => normalizeSelected(initialData?.invoices || []));
  // Mutations: general invoices fetch + linked invoices by parentPaymentId
  const [getLinkedInvoices, { data: linkedInvoicesData, isLoading: isLinkedLoading }] = useGetInvociesMutation();
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [invoicesRows, setInvoicesRows] = useState([]); // used by ApplyPaymentModal only
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [unlinkConfirm, setUnlinkConfirm] = useState({ open: false, row: null, nextSelection: null });
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  // Suppress auto-save briefly after programmatic submits (apply/unlink) to avoid duplicate submissions
  const suppressAutoSaveRef = React.useRef(false);
  // Closed & applied logic: if payment is closed (status closed/paid/complete) AND any amount applied (>0),
  // automatically filter invoices list by the payment's NAIC code (NOT by invoice number as previously implemented).
  const isClosedAndApplied = React.useMemo(() => {
    if (!initialData) return false;
    const statusVal = (initialData.status || '').toString().toLowerCase();
    const closed = ['closed', 'paid', 'complete'].includes(statusVal);
    const anyApplied = Number(initialData.appliedAmount) > 0; // loosened condition per request
    return closed && anyApplied && !!initialData.naicCode;
  }, [initialData]);

  const { skip } = useFetchWithMsal();
  const [getInovices, { data, isLoading }] = useGetInvociesMutation("", { skip });
  const [getPayments] = useGetPaymentsMutation("", { skip });

  // Prevent flicker: defer first fetch until we have initialData (when editing) AND invoice statuses loaded.
  // Legacy flag from previous auto-fetch logic; kept for potential future skeleton toggling.
  const [hasRequestedInitial, setHasRequestedInitial] = useState(true);
  const statusesReady = !!invoicesStatuses && invoicesStatuses.length > 0 && !isLoadingStatuses;
  const readyForInitialFetch = (isNew || !!initialData) && statusesReady;

  const roleId = useSelector((state) => state?.user?.roleId);

  // The Redux user slice stores the user id under `userId` (not `id`)
  const userId = useSelector((state) => state?.user?.userId);

  // Log user context after selectors are available (avoids calling hooks inside callbacks)
  useEffect(() => {
    try {
      // eslint-disable-next-line no-console
      console.debug('[AddPaymentPopup] user context:', { userId, roleId });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug('[AddPaymentPopup] user context (raw):', userId, roleId);
    }
  }, [userId, roleId]);

  // Main popup no longer auto-fetches invoices; fetching happens in Apply modal
  useEffect(() => {
    // noop: keep dependency list to avoid linter warnings
  }, [paginationModel.page, paginationModel.pageSize, sortingModel?.field, sortingModel?.sort, initialData, skip, readyForInitialFetch, hasRequestedInitial]);

  // Sync if the initialData reference changes (e.g. editing a different payment while dialog open)
  useEffect(() => {
    if (!open) return;

    // Reset working grid rows for fresh context each open
    setInvoicesRows([]);
    setTotalCount(0);
    setHasLoadedOnce(false);

    // Fetch linked invoices via parentPaymentId when editing existing payment
    if (initialData?.id) {
      // Clear stale before fetch
      setLinkedInvoices([]);
      setSelectedInvoices([]);
      getLinkedInvoices({ page: 1, pageSize: 500, parentPaymentId: initialData.id });
    } else {
      // New payment: clean slate
      setLinkedInvoices([]);
      setSelectedInvoices([]);
    }
  }, [open, initialData?.id, getLinkedInvoices]);

  // When linked invoices mutation returns data, hydrate local state
  useEffect(() => {
    console.debug('[AddPaymentPopup] linkedInvoicesData changed:', linkedInvoicesData);
    if (linkedInvoicesData?.invoices && Array.isArray(linkedInvoicesData.invoices)) {
      const arr = linkedInvoicesData.invoices.map(inv => ({
        ...inv,
        appliedAmount: inv.appliedAmount || inv.AppliedAmount || inv.amount || 0,
        AppliedAmount: inv.AppliedAmount || inv.appliedAmount || inv.amount || 0,
        Note: inv.Note || inv.note || ""
      }));
      setLinkedInvoices(arr);
      setSelectedInvoices(normalizeSelected(linkedInvoicesData.invoices));
    }
  }, [linkedInvoicesData]);

  // When popup closes, clear any lingering linked / selected invoices so a new open starts clean
  useEffect(() => {
    if (!open) {
      setLinkedInvoices([]);
      setSelectedInvoices([]);
    }
  }, [open]);

  // Merge filter-fetched invoices with persisted linked invoices; mark linked ones
  const mergedRows = React.useMemo(() => {
    const map = new Map();
    const linkedSet = new Set((linkedInvoices || []).map(r => r?.id));
    (invoicesRows || []).forEach(r => {
      const isLinked = linkedSet.has(r?.id);
      map.set(r?.id, { ...r, isLinked });
    });
    (linkedInvoices || []).forEach(r => {
      const prev = map.get(r?.id);
      if (prev) {
        map.set(r?.id, { ...prev, isLinked: true });
      } else {
        map.set(r?.id, { ...r, isLinked: true });
      }
    });
    return Array.from(map.values());
  }, [invoicesRows, linkedInvoices]);


  /*   useEffect(() => {
      if (open) {
        setSelectedInvoices(initialData?.invoices || []);
      }
    }, [initialData, open]);
  
    useEffect(() => {
      !open && setSelectedInvoices([])
    }, [open]); */

  useEffect(() => {
    if (!isLoading) {
      if (paymentOpenStatus) {
        const filtered = (data?.invoices || []).filter(inv => inv?.isOpen === true || inv?.isOpen);
        setTotalCount(filtered.length);
        setHasLoadedOnce(true);
        setInvoicesRows(filtered);
      } else {
        if (data?.totalCount !== totalCount) {
          setTotalCount(data?.totalCount);
        }
        if (data?.invoices) {
          setHasLoadedOnce(true);
          setInvoicesRows(data.invoices);
        }
      }
    }
  }, [isLoading, data?.totalCount, data?.invoices, totalCount, paymentOpenStatus]);


  // When editing a closed payment that is applied to an invoice, auto-select that invoice and populate applied amount
  useEffect(() => {
    if (!isClosedAndApplied) return;
    if (!initialData?.invoiceNumber) return;
    if (!invoicesRows?.length) return;
    // Already selected?
    const already = selectedInvoices?.length ? selectedInvoices.find(si => !!invoicesRows.find(r => r.id === si.id)) : null;
    if (already) return;
    const target = invoicesRows.find(r => String(r.invoiceNumber) === String(initialData.invoiceNumber) || String(r.noticeNumber) === String(initialData.invoiceNumber));
    if (target) {
      const appliedVal = Number(initialData.appliedAmount) || Number(initialData.paymentAmount) || 0;
      setSelectedInvoices([{ id: target.id, appliedAmount: appliedVal, AppliedAmount: appliedVal, Note: "" }]);
    }
  }, [isClosedAndApplied, initialData?.invoiceNumber, invoicesRows]);

  const fetchData = (value, overrideSearch) => {
    // If editing a closed payment, force invoice status filter to 'Paid' and ignore any user-selected status
    const isClosedPayment = initialData && ['closed', 'paid', 'complete'].includes((initialData.status || '').toLowerCase());
    const effectiveStatus = isClosedPayment ? 'Paid' : (status || 'Overdue');
    // Block premature fetch if closed payment needs statuses to resolve 'Paid' status id
    if (isClosedPayment && !statusesReady) return;
    const activeSearch = overrideSearch !== undefined ? overrideSearch : searchTerm;
    const trimmed = activeSearch?.trim();
    const isNaic = trimmed && /^\d+$/.test(trimmed);
    // Determine which status name to use for lookup
    const chosenStatusName = isClosedPayment ? 'Paid' : (value ? value.status : effectiveStatus);
    // Build status filter payload: prefer All Open -> blank statusId, else multi-select statusIds, else single statusId by name
    const statusPayload = (() => {
      const explicitStatusIds = value?.statusIds?.length ? value.statusIds : null;
      const hasValueStatus = value && typeof value.status !== 'undefined' && value.status !== null;
      const fromReduxIds = !hasValueStatus && !explicitStatusIds ? (statusIds?.length ? statusIds : null) : null;
      const allOpenSelected = value ? (value.status === 'All Open') : (status === 'All Open');
      // 1) If user picked specific statuses now -> send statusIds
      if (explicitStatusIds) return { statusIds: explicitStatusIds };
      // 2) If user picked a single status now (not All Open) -> send single statusId
      if (hasValueStatus && !allOpenSelected) {
        const id = findIdByName(invoicesStatuses, value.status);
        return id ? { statusId: id } : {};
      }
      // 3) If user picked All Open now -> send list of open status ids
      if (hasValueStatus && allOpenSelected) {
        const openIds = (invoicesStatuses || []).filter(s => s?.isOpen).map(s => s.id);
        return { statusIds: openIds };
      }
      // 4) Fall back to Redux: if Redux has multi-select ids, send them
      if (fromReduxIds) return { statusIds: fromReduxIds };
      // 5) Otherwise use Redux single status
      if (status === 'All Open') {
        const openIds = (invoicesStatuses || []).filter(s => s?.isOpen).map(s => s.id);
        return { statusIds: openIds };
      }
      const id = findIdByName(invoicesStatuses, chosenStatusName);
      return id ? { statusId: id } : {};
    })();

    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || "",
      orderByDirection: sortingModel?.sort || "",
      parentPaymentId: initialData?.id || null,
    };
    // Override filtering if payment is closed & applied: force NAIC code, drop invoiceNumber to broaden list to all invoices for that member
    if (isClosedAndApplied && initialData?.naicCode) {
      body.naicCode = initialData.naicCode;
      body.memberName = null; // ensure NAIC specificity
      body.invoiceNumber = null; // per request: no invoice-number filtering
    }
    try {
      // eslint-disable-next-line no-console
      console.debug('[AddPaymentPopup] fetch invoices body:', JSON.stringify(body));
    } catch { }
    getInovices(body);
  };

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };;

  const [addPayment, { isSuccess, error, reset: resetAddPayment }] = useAddPaymentMutation("", { skip });

  // Snackbar state for save result feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackCoords, setSnackCoords] = useState(null);

  // Reset mutation state when dialog opens again to avoid immediate auto-close from stale isSuccess
  useEffect(() => {
    if (open && typeof resetAddPayment === 'function') {
      resetAddPayment();
      // also clear any prior snackbar state for a clean start
      setSnackbarOpen(false);
      setSnackbarMessage("");
      setSnackbarSeverity("success");
    }
  }, [open, resetAddPayment]);

  // Removed auto-close on isSuccess; snackbar feedback is handled in handleSubmit
  // so the modal remains open after saving.

  useEffect(() => {
    if (error) {
      const msg = error?.data?.Message || error?.message || "Failed to save payment.";
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [error]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };
  // Removed auto-fill logic for remaining amount to allow partial application of payment.

  const handleSelectionChange = (newSelection) => {
    const prevSelectedIds = (selectedInvoices || []).map(si => si.id);
    const removedIds = prevSelectedIds.filter(id => !newSelection.includes(id));
    const addedIds = (newSelection || []).filter(id => !prevSelectedIds.includes(id));

    if (removedIds.length) {
      // Prompt unlink confirmation for the first removed id
      const removedId = removedIds[0];
      const row = (linkedInvoices || []).find(r => r.id === removedId) || null;
      setUnlinkConfirm({ open: true, row, nextSelection: newSelection });
      // Do not update selection now; keep previous selection until user confirms/cancels
      return;
    }

    // Only additions: update working selection immediately
    if (addedIds.length) {
      setSelectedInvoices((prev) => {
        const updatedSelection = (newSelection || []).map((id) => {
          const existing = prev?.find((item) => item.id === id);
          return existing || { id, appliedAmount: "", AppliedAmount: 0, Note: "" };
        });
        return updatedSelection;
      });
    }
  };

  const handleConfirmUnlink = () => {
    const targetId = unlinkConfirm?.row?.id;
    const nextSel = unlinkConfirm?.nextSelection || [];
    if (targetId == null) {
      setUnlinkConfirm({ open: false, row: null, nextSelection: null });
      return;
    }
    // Remove from linkedInvoices
    setLinkedInvoices(prev => (prev || []).filter(r => r.id !== targetId));
    // Align selectedInvoices to the provided next selection model
    setSelectedInvoices((prev) => {
      const updatedSelection = (nextSel || []).map((id) => {
        const existing = prev?.find((item) => item.id === id);
        return existing || { id, appliedAmount: "", AppliedAmount: 0, Note: "" };
      });
      return updatedSelection;
    });
    setUnlinkConfirm({ open: false, row: null, nextSelection: null });
    // Immediately submit after unlink to persist change
    suppressAutoSaveRef.current = true;
    setTimeout(() => { suppressAutoSaveRef.current = false; }, 1500);
    setTimeout(() => {
      if (formikRef.current?.submitForm) {
        formikRef.current.submitForm();
      }
    }, 0);
  };

  const handleCancelUnlink = () => {
    // Keep selection as it was; just close dialog
    setUnlinkConfirm({ open: false, row: null, nextSelection: null });
  };

  const handleRowUpdate = (newRow, oldRow) => {
    setSelectedInvoices((prev) => {
      if (!prev?.length) return prev;

      const totalOtherAmounts = prev?.reduce(
        (sum, item) => sum + (item.id === newRow.id ? 0 : parseFloat(item.appliedAmount || 0)),
        0
      );

      const remainingAmount = Math.max((initialValues?.paymentAmount || 0) - totalOtherAmounts, 0);
      const updatedAmount = Math.min(parseFloat(newRow.appliedAmount) || 0, remainingAmount);

      return prev?.map((item) =>
        item.id === newRow.id ? { ...item, appliedAmount: updatedAmount, AppliedAmount: updatedAmount } : item
      );
    });

    return {
      ...newRow,
      appliedAmount: Math.min(
        parseFloat(newRow.appliedAmount) || 0,
        Math.max((initialValues?.paymentAmount || 0) - (selectedInvoices?.reduce(
          (sum, item) => sum + (item.id === newRow.id ? 0 : parseFloat(item.appliedAmount || 0)),
          0
        ) || 0), 0)
      )
    };
  };

  const validationSchema = Yup.object().shape({
    paymentAmount: Yup.number()
      .transform(v => (Number.isNaN(v) ? undefined : v))
      .required("Required"),

    appliedAmount: Yup.number()
      .transform(v => (v == null || Number.isNaN(v) ? 0 : v))
      .test("not-over-applied", "Total Applied Amount exceeds Payment amount.", function (value) {
        const totalApplied = Number(value || 0);
        const payment = Number(this.parent?.paymentAmount || 0);
        return totalApplied <= payment;
      }),
  });

  const onApplyFilters = (value) => {
    // Filtering handled inside Apply modal
  };

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => {
      if (paginationModel.page !== 0) {
        setPaginationModel(p => ({ ...p, page: 0 }));
      } else {
        fetchData(undefined, searchTerm);
      }
    }, 400);
    return () => clearTimeout(h);
  }, [searchTerm]);

  const transformInvoices = (selectedInvoices) => {
    const createdAt = new Date().toISOString();
    // force keys to exist: use null/0 fallbacks so stringify won't drop them
    const createdBySafe = (userId ?? 0);          // pick a sensible fallback for your API
    return (selectedInvoices || []).map(({ id, appliedAmount, AppliedAmount, Note }) => ({
      invoiceId: id,
      createdBy: createdBySafe,                    // never undefined
      createdAt,                                   // always present
      appliedAmount: Number(
        AppliedAmount !== undefined ? AppliedAmount : (appliedAmount || 0)
      ),
      note: Note || "",
    }));
  };
  const handleSubmit = async (values) => {
    console.debug('[AddPaymentPopup] handleSubmit values:', values, 'selectedInvoices:', selectedInvoices);
    const appliedTotal = selectedInvoices?.reduce(
      (sum, item) => sum + Number(item?.appliedAmount || 0),
      0
    );

    const paymentAmountNum = Number(values?.paymentAmount || initialValues?.paymentAmount || 0);
    const isFullyApplied = paymentAmountNum > 0 && appliedTotal >= paymentAmountNum;

    const payload = {
      id: values?.id || currentPaymentId || undefined,
      paymentAmount: paymentAmountNum || undefined,
      appliedAmount: appliedTotal,
      comments: values?.comments,
      paymentType: values?.paymentType,
      dateReceived: values?.dateReceived,
      invoices: transformInvoices(selectedInvoices)
    };

    // If payment is fully applied, optimistically mark as Closed
    if (isFullyApplied) {
      payload.status = "Closed";
    }
    try {
      setSnackbarMessage("Saving payment…");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      const mutationResult = await addPayment(payload);
      const result = mutationResult?.unwrap ? await mutationResult.unwrap() : mutationResult;
      // Capture returned id for newly created payment and update local state
      const returnedId = result?.id || result?.paymentId || payload.id || formikRef.current?.values?.id || currentPaymentId;
      if (returnedId && !currentPaymentId) {
        setCurrentPaymentId(returnedId);
      }
      // Try canonical refresh via getPayments using paymentKey
      let refreshedPayment = null;
      try {
        const body = {
          page: 1,
          pageSize: 1,
          paymentKey: returnedId,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        const paymentsResp = await getPayments(body);
        console.debug('[AddPaymentPopup] refreshed payment response:', paymentsResp);
        const paymentsData = paymentsResp?.unwrap ? await paymentsResp.unwrap() : paymentsResp?.data || paymentsResp;
        refreshedPayment = paymentsData?.payments?.[0] || null;
      } catch (e) {
        // swallow refresh errors; fall back to local values
      }

      const currentValues = formikRef.current?.values || values;
      const refreshed = refreshedPayment ? {
        id: refreshedPayment?.id || refreshedPayment?.paymentId || returnedId || currentValues?.id || null,
        paymentAmount: Number(refreshedPayment?.paymentAmount ?? refreshedPayment?.amount ?? currentValues?.paymentAmount ?? 0),
        comments: refreshedPayment?.comments ?? currentValues?.comments ?? '',
        paymentType: refreshedPayment?.paymentType ?? currentValues?.paymentType ?? '',
        dateReceived: refreshedPayment?.dateReceived ?? currentValues?.dateReceived ?? '',
        status: refreshedPayment?.status ?? refreshedPayment?.paymentStatus ?? currentValues?.status,
        appliedAmount: Number(refreshedPayment?.appliedAmount ?? appliedTotal ?? 0),
        invoices: Array.isArray(currentValues?.invoices) ? currentValues.invoices : (selectedInvoices || []),
        naicCode: refreshedPayment?.naicCode ?? currentValues?.naicCode,
        invoiceNumber: refreshedPayment?.invoiceNumber ?? currentValues?.invoiceNumber,
      } : {
        id: returnedId || currentValues?.id || null,
        paymentAmount: (result?.paymentAmount ?? result?.amount ?? currentValues?.paymentAmount) || 0,
        comments: (result?.comments ?? currentValues?.comments) || '',
        paymentType: (result?.paymentType ?? currentValues?.paymentType) || '',
        dateReceived: result?.dateReceived ?? currentValues?.dateReceived ?? '',
        status: (result?.status ?? result?.paymentStatus ?? currentValues?.status),
        appliedAmount: (result?.appliedAmount ?? appliedTotal) || 0,
        invoices: Array.isArray(currentValues?.invoices) ? currentValues.invoices : (selectedInvoices || []),
        naicCode: currentValues?.naicCode,
        invoiceNumber: currentValues?.invoiceNumber,
      };
      console.log('[AddPaymentPopup] refreshed payment values after save:', refreshed);
      // Reset the form so UI reflects latest state and clears dirty
      if (formikRef.current?.resetForm) {
        formikRef.current.resetForm({ values: refreshed });
        // Explicitly ensure status is set from refreshed data for the pill
        if (refreshed?.status !== undefined && formikRef.current?.setFieldValue) {
          formikRef.current.setFieldValue('status', refreshed.status, false);
        }
      } else if (formikRef.current?.setValues) {
        formikRef.current.setValues(refreshed);
        if (refreshed?.status !== undefined && formikRef.current?.setFieldValue) {
          formikRef.current.setFieldValue('status', refreshed.status, false);
        }
      }
      setSnackbarMessage("Payment saved successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      // Intentionally keep the popup open after save (no auto-close)
      // Refresh linked invoices from API to reflect latest server-calculated fields
      const refreshId = returnedId || currentPaymentId || values?.id;
      if (refreshId) {
        try {
          getLinkedInvoices({ page: 1, pageSize: 500, parentPaymentId: refreshId });
        } catch (e) {
          // swallow refresh errors, show warning
          setSnackbarMessage("Payment saved, but failed to refresh linked invoices.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      }
    } catch (e) {
      const msg = e?.data?.Message || e?.message || "Failed to save payment.";
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Auto-save fallback: only after user touches a field and there are actual changes (dirty) to avoid submit-on-open.
  useEffect(() => {
    if (!open) return;
    const frm = formikRef.current;
    if (!frm) return;
    const { values, dirty, touched, submitForm } = frm;
    if (!values) return;
    const userTouched = !!(touched?.paymentAmount || touched?.comments);
    if (!userTouched) return; // skip initial open
    if (suppressAutoSaveRef.current) return; // prevent double submit when a programmatic submit just occurred
    if (Number(values.paymentAmount) > 0 && dirty) {
      const h = setTimeout(() => {
        submitForm?.();
      }, 800);
      return () => clearTimeout(h);
    }
  }, [open, formikRef.current?.dirty, formikRef.current?.touched?.paymentAmount, formikRef.current?.touched?.comments, formikRef.current?.values?.paymentAmount, formikRef.current?.values?.comments]);

  const getInputComponent = (item, values) => {
    switch (item?.type) {
      case "number":
        return (
          <FieldContainer>
            <InputField
              name={item.name}
              label={item.label}
              fullWidth
              mode="decimal"
              isDisabled
            />
          </FieldContainer>
        );
      case "textarea":
        return (
          <FieldContainer>
            <InputField
              name={item.name}
              label={item.label}
              fullWidth
              type="textarea"
              isDisabled
              width='100%'
            />
          </FieldContainer>
        );
      default:
        return (
          <FieldContainer>
            <InputField name={item.name} label={item.label} fullWidth />
          </FieldContainer>
        );
    }
  };

  const appliedAmount = React.useMemo(() => {
    return selectedInvoices?.reduce(
      (sum, item) => sum + parseFloat(item.appliedAmount || 0),
      0
    );
  }, [selectedInvoices]);
  const showLoadingOverlay = !hasLoadedOnce && (isLoading || isLoadingStatuses);

  // --- Helper components for summary section ---
  const StatusPill = ({ status }) => {
    if (!status) return <Box component="span">N/A</Box>;

    const label = status; // use exactly what the API/UI provides
    const isClosed = String(status).toLowerCase() === "closed";

    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          borderRadius: '15px',
          fontSize: 12,
          fontWeight: 600,
          bgcolor: isClosed ? '#dff6dd' : 'grey.300',
          color: isClosed ? 'success.dark' : 'text.primary',
          textTransform: 'capitalize',
          minWidth: 60,
          height: 22,
          lineHeight: 1,
          textAlign: 'center'
        }}
      >
        {label}
      </Box>
    );
  };
  const SummaryItem = ({ icon, label, value, full }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        gridColumn: full ? '1 / -1' : 'auto',
        minHeight: 30,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mt: '2px', color: 'primary.main' }}>{icon}</Box>
      <Box sx={{ fontWeight: 600 }}>{label}</Box>
      <Box sx={{ flex: 1 }}>{value}</Box>
    </Box>
  );

  return (
    <ModalWindow open={open} onClose={onClose} header={initialData ? "Edit Payment" : "Add Payment"}>
      {showLoadingOverlay && <LoadingPanel />}
      <Box ref={modalContentRef} sx={{ position: 'relative' }}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
        innerRef={formikRef}
      >
        {({ values, setFieldValue, errors }) => {
          if (values.appliedAmount !== appliedAmount) {
            setFieldValue("appliedAmount", appliedAmount);
          }

          return (
            <Form id={"addPayment"}>
              {/* Removed explicit Save/Cancel per request; dialog close via X in header */}
              <FormContainer>
                <FixedHeightBody>
                  <ScrollRegion>
                    {/* Top summary details with icons & pills */}
                    <Box sx={{ width: '900px', mb: 2 }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          columnGap: 3,
                          rowGap: 1.5,
                          p: 2.5,
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          backgroundColor: '#fafbff'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SummaryItem
                            icon={<InfoIcon fontSize="small" />}
                            label="Status:"
                            value={<StatusPill status={values?.status} />}
                          />
                          {(!isNew && String((values?.status ?? initialValues?.status) || '').toLowerCase() === 'open' && Number(appliedAmount || 0) === 0) && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => setVoidConfirmOpen(true)}
                              sx={{ ml: 1 }}
                            >
                              Void Payment
                            </Button>
                          )}
                        </Box>
                        {isNew ? (
                          <SummaryItem
                            icon={<CreditCardIcon fontSize="small" />}
                            label="Type:"
                            value={
                              <InputField
                                name="paymentType"
                                label="Payment Type"
                                fullWidth
                              />
                            }
                          />
                        ) : (
                          <SummaryItem
                            icon={(() => {
                              const pt = (initialValues?.paymentType || '').toLowerCase();
                              if (pt.includes('ach')) return <AccountBalanceIcon fontSize="small" />;
                              if (pt.includes('wire')) return <SwapHorizIcon fontSize="small" />;
                              if (pt.includes('check')) return <ReceiptLongIcon fontSize="small" />;
                              if (pt.includes('card') || pt.includes('credit')) return <CreditCardIcon fontSize="small" />;
                              return <CreditCardIcon fontSize="small" />; // default
                            })()}
                            label="Type:"
                            value={initialValues?.paymentType ?? 'N/A'}
                          />
                        )}
                        {isNew ? (
                          <SummaryItem
                            icon={<EventIcon fontSize="small" />}
                            label="Received:"
                            value={
                              <InputField
                                name="dateReceived"
                                label="Date Received"
                                fullWidth
                                type="date"
                              />
                            }
                          />
                        ) : (
                          <SummaryItem
                            icon={<EventIcon fontSize="small" />}
                            label="Received:"
                            value={initialValues?.dateReceived ? moment.utc(initialValues.dateReceived).local().format('MM/DD/YYYY') : 'N/A'}
                          />
                        )}
                        {isNew ? (
                          <SummaryItem
                            icon={<MonetizationOnIcon fontSize="small" />}
                            label="Amount:"
                            value={
                              <InputField
                                name="paymentAmount"
                                label="Payment Amount"
                                fullWidth
                                mode="decimal"
                                reserveHelperSpace
                              />
                            }
                          />
                        ) : (
                          <SummaryItem
                            icon={<MonetizationOnIcon fontSize="small" />}
                            label="Amount:"
                            value={
                              typeof initialValues?.paymentAmount === 'number'
                                ? initialValues.paymentAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                : (initialValues?.paymentAmount
                                  ? Number(initialValues.paymentAmount).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                  : 'N/A')
                            }
                          />
                        )}
                        {isNew ? (
                          <SummaryItem
                            icon={<CommentIcon fontSize="small" />}
                            label="Transaction Comments:"
                            value={
                              <InputField
                                name="comments"
                                label="Comments"
                                fullWidth
                                type="textarea"
                              />
                            }
                            full
                          />
                        ) : (
                          <SummaryItem
                            icon={<CommentIcon fontSize="small" />}
                            label="Transaction Comments:"
                            value={initialValues?.comments ?? 'N/A'}
                            full
                          />
                        )}
                      </Box>
                    </Box>
                    {/* Two-column form fields */}
                    {/*   <Box sx={{
                width: '800px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 3,
                rowGap: 2,
                mb: 2,
              }}>
                <FieldContainer style={{ width: '100%' }}>
                  <InputField
                    name="paymentAmount"
                    label="Payment Amount:"
                    fullWidth
                    mode="decimal"
                    isDisabled
                  />
                </FieldContainer>
                <FieldContainer style={{ width: '100%' , gridColumn: '1 / -1' }}>
                  <InputField
                    name="comments"
                    label="Comments:"
                    fullWidth
                    type="textarea"
                    isDisabled
                    width='100%'
                  />
                </FieldContainer>
              </Box> */}
                    {/*               <FieldContainer style={{ width: '100%' }}>
                  <InputField
                    name="appliedAmount"
                    label="Applied Amount:"
                    fullWidth
                    mode="decimal"
                    isDisabled
                    reserveHelperSpace
                    helperMinHeigh t={18}
                    error={!!errors.appliedAmount}
                  />
                </FieldContainer> */}
                    <Box display={"flex"} sx={{ alignItems: 'center', justifyContent: "space-between", width: '100%' }} mb={3} mt={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h1" color="primary.main">
                          Payment Allocation
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setShowApplyModal(true)}
                          disabled={Number(initialValues?.paymentAmount || 0) - Number(appliedAmount || 0) <= 0}
                        >
                          Apply
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<HistoryIcon fontSize="small" />}
                          onClick={() => setHistoryOpen(true)}
                          disabled={!(currentPaymentId || initialData?.id)}
                        >
                          History
                        </Button>
                      </Box>
                    </Box>
                    {/* Always show linked invoices grid; remove initial skeleton logic since filtering moved to Apply modal */}
                    {!hasRequestedInitial ? (
                      <Box width="900px" height="330px" sx={{ display: 'flex', flexDirection: 'column', gap: 1, pr: 1 }}>
                        <Skeleton variant="rounded" height={34} animation="wave" />
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Skeleton key={i} variant="rounded" height={46} animation="wave" />
                        ))}
                      </Box>
                    ) : (
                      <StyledGrid
                        data={linkedInvoices}
                        width="900px"
                        height="330px"
                        overflow="auto"
                        columns={invoicesColumns}
                        rowCount={linkedInvoices?.length || 0}
                        withFilter={false}
                        withCheckboxSelection
                        onSortingModelChange={(model) => setSortingModel(model[0])}
                        pageSizeOptions={[5]}
                        paginationModel={paginationModel}
                        setPaginationModel={handlePaginationModelChange}
                        onRowSelectionModelChange={handleSelectionChange}
                        selectionModel={selectedInvoices?.map((item) => item.id) || []}
                        hideFooterSelectedRowCount={false}
                        processRowUpdate={handleRowUpdate}
                        isCellEditable={(params) =>
                          params.field === "appliedAmount" &&
                          selectedInvoices?.some((item) => item.id === params.row.id)
                        }
                      />
                    )}
                    {/* Bottom total applied summary */}
                    <Box sx={{ width: '900px', display: 'flex', paddingRight: 1, justifyContent: 'flex-end', mt: 1.5 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: 16 }}>Total Applied:</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 16 }} color="primary.main">
                          {appliedAmount ? Number(appliedAmount).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '$0.00'}
                        </Typography>
                        {Number(appliedAmount || 0) === Number(values?.paymentAmount || 0) && (
                          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        )}
                      </Box>
                    </Box>
                  </ScrollRegion>
                </FixedHeightBody>
              </FormContainer>
            </Form>
          );
        }}
      </Formik>
      <ApplyPaymentModal
        open={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        totalAmount={initialValues?.paymentAmount}
        alreadyApplied={selectedInvoices?.reduce((sum, i) => sum + Number(i?.appliedAmount || 0), 0)}
        onApply={(rows) => {
          // rows: array of invoice rows selected in modal
          const paymentTotal = Number(initialValues?.paymentAmount || 0);
          const currentlyApplied = (selectedInvoices || []).reduce((sum, si) => sum + Number(si.appliedAmount || 0), 0);
          let remaining = Math.max(paymentTotal - currentlyApplied, 0);

          if (remaining <= 0) {
            setSnackbarMessage("funds already applied!");
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return; // keep modal open so user can adjust
          }

          console.debug('[AddPaymentPopup] Applying invoices from modal:', rows, 'with remaining amount:', remaining);

          // Helper to get per-invoice cap (balance) without exceeding undefined
          const getCap = (r) => {
            const candidates = [r?.remainingAmount, r?.balance, r?.outstanding, r?.amountDue, r?.invoiceBalance, r?.amount];
            for (const c of candidates) {
              const n = Number(c);
              if (!Number.isNaN(n) && n > 0) return n;
            }
            return Number.POSITIVE_INFINITY; // if unknown, don't cap by invoice
          };

          const toAddFull = (rows || []).map(r => {
            const existing = Number(r.appliedAmount || r.AppliedAmount || 0);
            const cap = getCap(r);
            // If user already typed a value in the modal, respect it but cap by remaining and invoice cap
            let assign = existing > 0 ? existing : Math.min(remaining, cap);
            assign = Math.min(assign, remaining, cap);
            // Guard against negative/NaN
            assign = Math.max(Number.isFinite(assign) ? assign : 0, 0);
            remaining -= assign;
            return { ...r, appliedAmount: assign, AppliedAmount: assign, Note: r.Note || r.note || "" };
          });
          const toSelect = toAddFull.map(r => ({ id: r.id, appliedAmount: r.appliedAmount, AppliedAmount: r.AppliedAmount, Note: r.Note || "" }));

          // Merge full rows into linkedInvoices so grid has all display fields
          setLinkedInvoices(prev => {
            const map = new Map((prev || []).map(x => [x.id, x]));
            toAddFull.forEach(x => {
              const existing = map.get(x.id);
              if (!existing) {
                map.set(x.id, { ...x, isLinked: true });
              } else {
                // Update existing with any newly set applied amounts
                map.set(x.id, { ...existing, appliedAmount: x.appliedAmount, AppliedAmount: x.AppliedAmount, Note: x.Note, isLinked: true });
              }
            });
            return Array.from(map.values());
          });
          // Keep selectedInvoices in sync with linkedInvoices (so editing is enabled)
          setSelectedInvoices(prev => {
            const map = new Map((prev || []).map(x => [x.id, x]));
            toSelect.forEach(x => {
              map.set(x.id, x); // add or overwrite with latest amounts from modal
            });
            return Array.from(map.values());
          });
          setShowApplyModal(false);
          // Auto-submit after applying selected invoices to persist immediately
          suppressAutoSaveRef.current = true;
          setTimeout(() => { suppressAutoSaveRef.current = false; }, 1500);
          setTimeout(() => {
            if (formikRef.current?.submitForm) {
              formikRef.current.submitForm();
            }
          }, 0);
        }}
      />
      <PaymentHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        paymentKey={currentPaymentId || initialData?.id || null}
      />
      <Dialog open={unlinkConfirm.open} onClose={handleCancelUnlink} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Unlink</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Unlink invoice {unlinkConfirm?.row?.noticeNumber || unlinkConfirm?.row?.invoiceNumber || unlinkConfirm?.row?.id}
            {unlinkConfirm?.row?.memberName ? ` (${unlinkConfirm.row.memberName})` : ''} from payment?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancelUnlink}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmUnlink}>Unlink</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={voidConfirmOpen} onClose={() => setVoidConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Void Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to void this payment? This action will mark the payment as Voided.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setVoidConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              try {
                if (formikRef.current?.setFieldValue) {
                  formikRef.current.setFieldValue('status', 'Voided');
                }
                setVoidConfirmOpen(false);
                suppressAutoSaveRef.current = true;
                setTimeout(() => { suppressAutoSaveRef.current = false; }, 1500);
                setTimeout(() => {
                  if (formikRef.current?.submitForm) {
                    formikRef.current.submitForm();
                  }
                }, 0);
              } catch (e) {
                setVoidConfirmOpen(false);
              }
            }}
          >
            Void
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={(theme) => ({ zIndex: (theme.zIndex.modal || 1300) + 10 })}
        container={() => modalContentRef.current}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%', fontSize: '0.85rem', py: 1 }}
        >
          <span style={{ fontSize: '0.85rem' }}>{snackbarMessage}</span>
        </Alert>
      </Snackbar>
      </Box>
    </ModalWindow>
  );
};
